import { useId, useState } from "react";
import { MAX_TAGS_PER_ITEM, normaliseTags, type ReserveTag, type TagCount } from "@iltocat/shared";
import { Icon } from "./Icon.js";

/**
 * Tags as chips with a box at the end. Enter or a comma turns what is typed
 * into a chip; backspace in an empty box takes the last one off; leaving the
 * box keeps what is in it, because a tag typed and then tabbed away from is a
 * tag somebody meant.
 *
 * Suggestions are the browser's own `<datalist>` over the tags that already
 * exist -- one control less to build and it does the right thing with a phone
 * keyboard. `normaliseTags` is the same function the worker uses, so what the
 * chips show is what will be stored: a `#` stripped, a repeat in another case
 * gone.
 *
 * A stockroom's reserve tags are offered under the box, a click each. Past a
 * few they fold behind "N more": typing finds them through the suggestions
 * anyway, and a long row of chips under every item's tags is noise.
 */
const OFFERED = 6;

export function TagInput({
  tags,
  onChange,
  known,
  reserve = [],
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
  known: TagCount[];
  /** A stockroom's reserve tags: worn with a tag icon, and one click away below the box. */
  reserve?: ReserveTag[];
}) {
  const [text, setText] = useState("");
  const [unfolded, setUnfolded] = useState(false);
  const listId = useId();
  const isReserve = (name: string) => reserve.some((tag) => tag.name.toLowerCase() === name.toLowerCase());
  const wearing = new Set(tags.map((tag) => tag.toLowerCase()));
  const offered = reserve.filter((tag) => !wearing.has(tag.name.toLowerCase()));

  const commit = (raw: string) => {
    const next = normaliseTags([...tags, ...raw.split(",")]);
    onChange(next);
    setText("");
  };

  return (
    <div className="field">
      <label className="field-label" htmlFor={`${listId}-input`}>
        Tags
      </label>
      <div className="tag-box">
        {tags.map((tag) => (
          <span key={tag} className={isReserve(tag) ? "tag tag-reserve" : "tag"}>
            {isReserve(tag) && <Icon name="tag" size={12} />}
            {tag}
            <button
              type="button"
              className="tag-remove"
              aria-label={`Remove tag ${tag}`}
              onClick={() => onChange(tags.filter((entry) => entry !== tag))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={`${listId}-input`}
          className="tag-text"
          value={text}
          list={listId}
          placeholder={tags.length === 0 ? "power tools, garage…" : ""}
          disabled={tags.length >= MAX_TAGS_PER_ITEM}
          onChange={(event) => {
            const value = event.target.value;
            // A comma anywhere is "that one is done".
            if (value.includes(",")) commit(value);
            else setText(value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              // Enter here must not submit the form it is inside.
              event.preventDefault();
              if (text.trim()) commit(text);
            } else if (event.key === "Backspace" && text === "" && tags.length > 0) {
              onChange(tags.slice(0, -1));
            }
          }}
          onBlur={() => {
            if (text.trim()) commit(text);
          }}
        />
        <datalist id={listId}>
          {known
            .filter((tag) => !tags.some((entry) => entry.toLowerCase() === tag.name.toLowerCase()))
            .map((tag) => (
              <option key={tag.id} value={tag.name} />
            ))}
        </datalist>
      </div>
      {offered.length > 0 && tags.length < MAX_TAGS_PER_ITEM && (
        <div className="tag-reserve-offer">
          <span className="muted small">
            <Icon name="tag" size={12} /> Reserve tags:
          </span>
          {(unfolded ? offered : offered.slice(0, OFFERED)).map((tag) => (
            <button
              key={tag.name}
              type="button"
              className="chip chip-small"
              onClick={() => onChange(normaliseTags([...tags, tag.name]))}
            >
              {tag.name}
            </button>
          ))}
          {!unfolded && offered.length > OFFERED && (
            <button type="button" className="reserve-pick-more" onClick={() => setUnfolded(true)}>
              {offered.length - OFFERED} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
